"use client";

import { useState } from "react";
import { postJson } from "@lib/http";
import { useRouter } from "next/navigation";

import { useTranslations } from "@i18n/provider";
import { useOptionalAgentOverlayContext } from "./AgentOverlayProvider";
import { captureScreenshot } from "./captureScreenshot";
import {
  NAVIGATE_DESTINATIONS,
  type AgentAction,
  type AgentActionStatus,
} from "@lib/agent/actionToken";
import { lessonTypeAccent } from "@lib/learner/hubTints";
import { modalityToLessonType } from "@lib/learner/modality/modality";
import { GhostButton, PrimaryButton } from "@ui/buttons";
import { patchPreferences } from "@lib/userPreferencesClient";
import { modalityNounKey } from "@lib/learner/modality/labels";

/**
 * Confirm chip for a tutor-proposed action (D-13). The D-14 guarantee is
 * structural: the ONLY side effect lives inside the "Do it" tap handler -
 * the chip never auto-fires, and its fetches hit the same authenticated
 * routes the on-page controls use. "Not now" dismisses silently.
 *
 * The outcome is owned by the caller (CON-123), so it survives a reload and
 * reaches the model. Only `busy` stays here: an in-flight fetch is transient,
 * and persisting it would strand a restored chip mid-flight forever.
 */
export function AgentActionChip({
  action,
  status,
  onStatusChange,
}: {
  action: AgentAction;
  status: AgentActionStatus;
  onStatusChange: (status: AgentActionStatus) => void;
}) {
  const { t } = useTranslations();
  const router = useRouter();
  const overlay = useOptionalAgentOverlayContext();
  const [busy, setBusy] = useState(false);

  if (status === "dismissed") {
    return null;
  }

  const isBoardAction = action.type === "regenerate_slot";

  const proposal = (() => {
    switch (action.type) {
      case "regenerate_slot":
        return t("agent.actionChip.regenerate", {
          modality: t(modalityNounKey(action.modality)),
        });
      case "navigate":
        return t("agent.actionChip.navigate", {
          destination: t(`agent.actionChip.dest.${action.destination}`),
        });
      case "open_lesson":
        return t("agent.actionChip.openLesson", {
          title: action.title ?? t("agent.actionChip.openLessonFallbackTitle"),
        });
      case "set_preference":
        return t("agent.actionChip.setPreference", {
          key: action.key,
          value: String(action.value),
        });
      case "report_issue":
        return t("agent.actionChip.reportIssue", { summary: action.summary });
    }
  })();

  const doneMessage = (() => {
    switch (action.type) {
      case "regenerate_slot":
        return t("agent.actionChip.done");
      case "set_preference":
        return t("agent.actionChip.donePreference");
      case "report_issue":
        return t("agent.actionChip.doneReport");
      default:
        return t("agent.actionChip.doneGeneric");
    }
  })();

  const failedMessage = isBoardAction
    ? t("agent.actionChip.failed")
    : t("agent.actionChip.failedGeneric");

  const execute = async () => {
    setBusy(true);
    try {
      switch (action.type) {
        case "regenerate_slot": {
          const res = await postJson("/api/curriculum/board/regenerate", {
            lessonType: modalityToLessonType(action.modality),
          });
          onStatusChange(res.ok ? "confirmed" : "failed");
          break;
        }
        case "navigate": {
          onStatusChange("confirmed");
          router.push(NAVIGATE_DESTINATIONS[action.destination]);
          break;
        }
        case "open_lesson": {
          onStatusChange("confirmed");
          router.push(action.href);
          break;
        }
        case "set_preference": {
          const res = await patchPreferences({ [action.key]: action.value });
          onStatusChange(res.ok ? "confirmed" : "failed");
          if (res.ok) router.refresh();
          break;
        }
        case "report_issue": {
          const screenshot = await captureScreenshot();
          const res = await postJson("/api/feedback/report", {
            summary: action.summary,
            path: window.location.pathname,
            page: overlay?.getPromptPayload().pageContext?.page,
            ...(screenshot ? { screenshot } : {}),
          });
          onStatusChange(res.ok ? "confirmed" : "failed");
          break;
        }
      }
    } catch {
      onStatusChange("failed");
    } finally {
      setBusy(false);
    }
  };

  const accent = isBoardAction ? lessonTypeAccent(modalityToLessonType(action.modality)) : "";

  return (
    <div
      aria-busy={busy ? true : undefined}
      className={`surface-card mt-1 max-w-[85%] rounded-[20px] p-4 ${accent}`}
    >
      {status === "confirmed" ? (
        <p className="flex items-center gap-2 text-sm text-ink-soft">
          <span className="material-symbols-outlined text-[20px]" aria-hidden="true">
            check_circle
          </span>
          {doneMessage}
        </p>
      ) : status === "failed" ? (
        <p className="text-sm text-ink-soft">{failedMessage}</p>
      ) : (
        <>
          <p className="text-base text-ink-body">{proposal}</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <PrimaryButton onClick={() => void execute()} disabled={busy} className="btn-compact">
              {t("agent.actionChip.doIt")}
            </PrimaryButton>
            <GhostButton
              onClick={() => onStatusChange("dismissed")}
              disabled={busy}
              className="btn-compact"
            >
              {t("agent.actionChip.notNow")}
            </GhostButton>
          </div>
        </>
      )}
    </div>
  );
}
